import analytics from '../connectors/analytics';
import messages from '../constants/logMessages.json';
import { initQAL } from '../init/init';
import { engagementTrackingInit } from '../lib/engagementTracking';
import {
  AnalyticsWrapperProps,
  SendQALErrorsToDynatrace,
} from '../types/configs';
import { jsonToString } from '../utils/analyticsUtils';
import dimensionMetricValidation from '../utils/googleApi/index';
import {
  handleDynatraceManualPageDetection,
  isStandalone,
  onDtrumReady,
} from '../utils/helpers';
import { initialiseMfeEventController } from '../utils/helpers/mfeEventBusHelpers';
import logging, { getMessage } from '../utils/logging';
import tools from '../utils/tools';
import { getQbeTrack, setConfig } from './config';
import { runConfigValidation } from './configValidations';
import { initialProps } from './initialProps';

const {
  infoHeading,
  warnHeading,
  infoRegular,
  warnBold,
  infoDtHeading,
  infoDtBold,
} = messages;

/**
 * Initialises and configures QAL for an application runtime.
 *
 * Call once during app bootstrap before firing analytics events. The helper initialises QAL logging
 * and optional `window.QAL`, merges runtime config with `window.qbeTrack.meta` through `initialProps`
 * unless invoked by `AnalyticsWrapper`, validates common config mistakes, stores app start time,
 * configures feature flags, prepares milestone persistence, initialises MFE event handling, and can
 * enable Dynatrace page/session-property side effects.
 *
 * Configuration precedence: direct config values are merged with any host-provided
 * `window.qbeTrack.meta` values by `initialProps`; use direct values for runtime-only data that is
 * not known in the document head. Keep `initialData`, `bypassPiiCheckKeys`, and Dynatrace key
 * allowlists narrow and solution-design approved.
 *
 * @param _config - Application metadata, feature flags, Dynatrace options, MFE options, and optional
 * initial data for future payloads.
 * @param isWrapper - Internal flag used by `AnalyticsWrapper` after it has already applied
 * `initialProps`. Defaults to `false` for normal direct use.
 * @returns The hydrated runtime configuration stored by QAL.
 *
 * @example
 * configAnalytics({
 *   appName: 'repairer search',
 *   appEnv: 'prod',
 *   appPartner: 'QBE Direct',
 *   basePagePath: '/au/claims/repairer-search',
 *   enableApiStartEvent: true,
 *   sendPageViewToDynatrace: true,
 * });
 */
export const configAnalytics = (
  _config: AnalyticsWrapperProps,
  isWrapper = false
) => {
  // Auto-initialize QAL with options from config
  const { qalEnv, showQalBanner, exposeQalGlobal = true, appEnv } = _config;
  initQAL({
    env: qalEnv || appEnv,
    showQalBanner,
    exposeQalGlobal,
  });

  // Use initialProps only if isWrapper is false
  const config = isWrapper ? _config : initialProps(_config);

  runConfigValidation(config);
  logging.info(
    getMessage('analyticsWrapper', [jsonToString(config)]),
    infoHeading,
    infoRegular
  );

  const appStartTime = tools.getNow();
  const updatedConfig = setConfig({
    analyticsTime: { appStartTime },
    ...config,
  });
  const qbeTrack = getQbeTrack();

  const { omitDataObject, omitEvents } = logging.getLogOptions();

  // Check if any logging restrictions
  if (omitDataObject) {
    logging.info(getMessage('logOmitsDataObject'), warnHeading, warnBold);
  }

  if (Array.isArray(omitEvents) && omitEvents.length > 0) {
    logging.info(
      getMessage('logOmitsEvents', [omitEvents.toString()]),
      warnHeading,
      warnBold
    );
  }

  // Check if site tracking is disabled
  if (!updatedConfig.status) {
    logging.info(getMessage('siteTrackingDisabled'), warnHeading, warnBold);
    return updatedConfig;
  }

  // Set default appName if not provided
  if (!updatedConfig.appName) {
    updatedConfig.appName = 'development';
  }
  /**
   * If application is
   */
  // if (updatedConfig.appName.toUpperCase() === 'DEVELOPMENT') {
  //   logging.info(getMessage('appNameNotSet', []), warnHeading, warnBold);
  // }

  // Enable Google Spreadsheet pulling in development environment
  if (updatedConfig.appEnv === 'development') dimensionMetricValidation();

  // Update initialData in qbeTrack meta
  if (updatedConfig.initialData) {
    qbeTrack.meta.initialData = updatedConfig.initialData;
  }
  /**
   * reset site tracking session hook
   *
   * Boolean value passed from consumer
   * Used to determine is session should be reset
   */
  if (updatedConfig.shouldReset && updatedConfig?.cache?.clear) {
    updatedConfig.cache.clear();
    tools.resetAnalyticsTime();
  }

  // Disable engagement tracking if specified
  if (
    Object.prototype.hasOwnProperty.call(
      updatedConfig,
      'enableAdsEngagementTracking'
    ) &&
    updatedConfig.enableAdsEngagementTracking === false
  ) {
    /* Temporary disable of ADS Engagement tracking warning
    logging.info(
      getMessage('engagementTrackingDisabled'),
      warnHeading,
      warnBold
    ); */
    qbeTrack.meta.enableAdsEngagementTracking =
      updatedConfig.enableAdsEngagementTracking;
  }

  // Disable milestone form engagement if specified
  if (
    Object.prototype.hasOwnProperty.call(
      updatedConfig,
      'resetMilestoneFormEngagedDefault'
    ) &&
    updatedConfig.resetMilestoneFormEngagedDefault === false
  ) {
    logging.info(
      getMessage('resetFormEngagementDisabled'),
      warnHeading,
      warnBold
    );
    qbeTrack.meta.resetMilestoneFormEngagedDefault =
      updatedConfig.resetMilestoneFormEngagedDefault;
  }

  // Disable API start event if specified
  if (
    Object.prototype.hasOwnProperty.call(config, 'enableApiStartEvent') &&
    updatedConfig.enableApiStartEvent === false
  ) {
    logging.info(getMessage('apiStartEventDisabled'), warnHeading, warnBold);
    qbeTrack.meta.enableApiStartEvent = updatedConfig.enableApiStartEvent;
  }

  // Enable milestones persist if specified
  if (
    Object.prototype.hasOwnProperty.call(config, 'enableMilestonesPersist') &&
    updatedConfig.enableMilestonesPersist === true
  ) {
    logging.info(getMessage('milestonesPersistEnabled'), warnHeading, warnBold);
    qbeTrack.meta.enableMilestonesPersist =
      updatedConfig.enableMilestonesPersist;
    // Initialise function to load all persisted analytics milestone and analytics time data point to the Config.
    tools.persistConfigInit();
  }

  // Enable sending page view to Dynatrace if specified
  if (
    Object.prototype.hasOwnProperty.call(
      updatedConfig,
      'sendPageViewToDynatrace'
    ) &&
    updatedConfig.sendPageViewToDynatrace === true
  ) {
    qbeTrack.meta.sendPageViewToDynatrace =
      updatedConfig.sendPageViewToDynatrace;
  }

  // Update data layer keys to send to Dynatrace if specified
  if (
    Object.prototype.hasOwnProperty.call(
      updatedConfig,
      'sendDataLayerKeysToDynatrace'
    ) &&
    updatedConfig.sendDataLayerKeysToDynatrace &&
    updatedConfig.sendDataLayerKeysToDynatrace.length > 0
  ) {
    logging.info(
      getMessage('dataLayerKeysSentToDynatrace', [
        jsonToString(updatedConfig.sendDataLayerKeysToDynatrace),
      ]),
      infoDtHeading,
      infoDtBold
    );
    qbeTrack.meta.sendDataLayerKeysToDynatrace =
      updatedConfig.sendDataLayerKeysToDynatrace;
  }

  // Update QA errors to send to Dynatrace if specified
  if (
    Object.prototype.hasOwnProperty.call(
      updatedConfig,
      'sendQALErrorsToDynatrace'
    )
  ) {
    const sendQALErrors = updatedConfig['sendQALErrorsToDynatrace'] as
      | boolean
      | SendQALErrorsToDynatrace;
    if (!sendQALErrors) {
      logging.info(
        getMessage('sendQALErrorsToDynatrace'),
        warnHeading,
        warnBold
      );
    }
    qbeTrack.meta['sendQALErrorsToDynatrace'] = sendQALErrors;
  }

  // Update PII check keys if specified
  if (
    Object.prototype.hasOwnProperty.call(updatedConfig, 'bypassPiiCheckKeys') &&
    updatedConfig.bypassPiiCheckKeys &&
    updatedConfig.bypassPiiCheckKeys.length > 0
  ) {
    logging.info(
      getMessage('sendPiiDataLayerKey', [
        jsonToString(updatedConfig.bypassPiiCheckKeys),
      ]),
      warnHeading,
      warnBold
    );
    qbeTrack.meta.bypassPiiCheckKeys = updatedConfig.bypassPiiCheckKeys;
  }
  // Check if sendQbeTrackMetaKeysToDynatrace exists in config
  if (config.sendQbeTrackMetaKeysToDynatrace) {
    analytics.logDynatraceMetaData();
  }

  // Initialise QAL built-in engagement tracking for normal SPA, shell, and
  // standalone MFE runtimes. Embedded MFEs are initialised by the MFE event
  // controller below so the document listeners are only attached once.
  const isEmbeddedMfe =
    updatedConfig.enableQalMfe &&
    updatedConfig.qalMfeConfig?.mfeType === 'mfe' &&
    !isStandalone;

  const enableEngagementTracking =
    (updatedConfig.enableQalInBuiltEngagementTracking ??
      updatedConfig.enableInBuiltEngagementTracking ??
      false) === true;

  qbeTrack.meta.enableQalInBuiltEngagementTracking = enableEngagementTracking;

  if (enableEngagementTracking && !isEmbeddedMfe) {
    engagementTrackingInit();
  }

  initialiseMfeEventController(updatedConfig);

  // Call Dynatrace manual page detection if enabled
  const { sendPageViewToDynatrace, enableDynatraceManualPageDetection } =
    config;
  if (sendPageViewToDynatrace || enableDynatraceManualPageDetection) {
    onDtrumReady(() => {
      handleDynatraceManualPageDetection();
    });
  }

  return updatedConfig;
};
